"use client";

import { useRef } from "react";
import { useStatsigClient } from "./use-statsig-client";
import { useOnVisible } from "./use-on-visible";

/**
 * Fire `screen_viewed` for a wizard `step`, gated on the screen actually being
 * visible — riding the same {@link useOnVisible} signal as `content_visible`
 * (PE-1534).
 *
 * Owlet warm-loads Felt pages at app launch, so a wizard step can mount minutes
 * before — or entirely without — the user seeing it. Firing on mount would
 * count views nobody saw. Instead this fires only once the step is on screen,
 * and if it's currently parked off-screen, defers until it becomes visible.
 *
 * Centralizing the fire timing here (rather than a per-wizard `useEffect`) keeps
 * this visibility contract in one place across all four home/intake wizards.
 *
 * @example
 *   useScreenViewed(state.step);
 */
export function useScreenViewed(step: string): void {
  const { logEvent } = useStatsigClient();
  // The last step actually logged. Re-entering a prior step re-fires, matching
  // both the original per-wizard effect and `content_visible`'s per-route dedup.
  const loggedStep = useRef<string | null>(null);

  useOnVisible(
    () => {
      if (loggedStep.current === step) return;
      loggedStep.current = step;
      logEvent({ eventName: "screen_viewed", value: step });
    },
    { watch: [step] }
  );
}
